import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { IoMenu } from "react-icons/io5";
import { RxCross2 } from "react-icons/rx";
import {
  HiUser,
  HiCog,
  HiArrowRightOnRectangle,
  HiHome,
  HiWrenchScrewdriver,
  HiShieldCheck,
  HiDocumentText,
  HiInformationCircle,
} from "react-icons/hi2";
import { useMyContext } from "../store/ContextApi";
import logo from "../logo.png";
import { motion, AnimatePresence } from "framer-motion";

const Navbar = () => {
  const [headerToggle, setHeaderToggle] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathName = useLocation().pathname;
  const navigate = useNavigate();
  const { token, setToken, setCurrentUser, isAdmin, setIsAdmin } =
    useMyContext();

  const handleLogout = () => {
    localStorage.clear();
    setToken(null);
    setCurrentUser(null);
    setIsAdmin(false);
    navigate("/login");
  };

  useEffect(() => {
    const publicPaths = [
      "/",
      "/privacy",
      "/terms",
      "/disclaimer",
      "/about",
      "/login",
      "/used-bike-list-router",
      "/new-bike-list-router",
      "/usedBikeDetailShort",
      "/newBikeDetailShort",
    ];
    if (!localStorage.getItem("JWT_TOKEN") && !publicPaths.includes(pathName)) {
      navigate("/");
    }
  }, [token, pathName]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://www.googletagmanager.com/gtag/js?id=G-VN18C7ERM9";
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;

    window.gtag("js", new Date());
    window.gtag("config", "G-VN18C7ERM9");
  }, []);

  const navItems = [
    {
      path: "/my-bikes",
      label: "My Bikes",
      icon: <HiHome className="w-4 h-4" />,
    },
    {
      path: "/workshops",
      label: "Workshops",
      icon: <HiWrenchScrewdriver className="w-4 h-4" />,
    },
    {
      path: "/privacy",
      label: "Privacy",
      icon: <HiShieldCheck className="w-4 h-4" />,
    },
    {
      path: "/disclaimer",
      label: "Disclaimer",
      icon: <HiDocumentText className="w-4 h-4" />,
    },
    {
      path: "/terms",
      label: "Terms",
      icon: <HiDocumentText className="w-4 h-4" />,
    },
    {
      path: "/about",
      label: "About",
      icon: <HiInformationCircle className="w-4 h-4" />,
    },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200"
          : "bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 shadow-soft"
      }`}
    >
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <img
                  src={logo}
                  alt="Blue Bike Logo"
                  className="h-12 w-12 rounded-xl shadow-md group-hover:shadow-lg transition-all duration-300"
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-accent-400 to-accent-600 opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
              </div>
              <div className="flex flex-col">
                <h3
                  className={`text-2xl font-bold tracking-tight transition-colors duration-300 ${
                    isScrolled ? "text-gray-900" : "text-white"
                  }`}
                >
                  Blue Bike
                </h3>
                <span
                  className={`text-xs font-medium transition-colors duration-300 ${
                    isScrolled ? "text-gray-500" : "text-accent-200"
                  }`}
                >
                  Your Ride, Your Way
                </span>
              </div>
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <motion.div
                key={item.path}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    pathName === item.path
                      ? isScrolled
                        ? "bg-primary-100 text-primary-700 shadow-sm"
                        : "bg-white/20 text-white shadow-md"
                      : isScrolled
                      ? "text-gray-600 hover:text-primary-600 hover:bg-gray-50"
                      : "text-white/90 hover:text-white hover:bg-white/10"
                  }`}
                >
                  {item.icon}
                  {item.label}
                </Link>
              </motion.div>
            ))}
          </div>

          {/* User Actions */}
          <div className="hidden lg:flex items-center gap-3">
            {token ? (
              <>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/profile"
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      pathName === "/profile"
                        ? isScrolled
                          ? "bg-primary-100 text-primary-700 shadow-sm"
                          : "bg-white/20 text-white shadow-md"
                        : isScrolled
                        ? "text-gray-600 hover:text-primary-600 hover:bg-gray-50"
                        : "text-white/90 hover:text-white hover:bg-white/10"
                    }`}
                  >
                    <HiUser className="w-4 h-4" />
                    Profile
                  </Link>
                </motion.div>

                {isAdmin && (
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Link
                      to="/admin/recent-users"
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                        pathName.startsWith("/admin")
                          ? isScrolled
                            ? "bg-primary-100 text-primary-700 shadow-sm"
                            : "bg-white/20 text-white shadow-md"
                          : isScrolled
                          ? "text-gray-600 hover:text-primary-600 hover:bg-gray-50"
                          : "text-white/90 hover:text-white hover:bg-white/10"
                      }`}
                    >
                      <HiCog className="w-4 h-4" />
                      Admin
                    </Link>
                  </motion.div>
                )}

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium bg-red-500 hover:bg-red-600 text-white shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <HiArrowRightOnRectangle className="w-4 h-4" />
                  Logout
                </motion.button>
              </>
            ) : (
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/login"
                  className="flex items-center gap-2 px-6 py-2 rounded-lg font-semibold bg-accent-500 hover:bg-accent-600 text-white shadow-md hover:shadow-lg transition-all duration-200"
                >
                  Get Started
                </Link>
              </motion.div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setHeaderToggle(!headerToggle)}
            className={`lg:hidden p-2 rounded-lg transition-all duration-200 ${
              isScrolled
                ? "text-gray-600 hover:text-primary-600 hover:bg-gray-100"
                : "text-white hover:bg-white/10"
            }`}
          >
            {headerToggle ? (
              <RxCross2 className="w-6 h-6" />
            ) : (
              <IoMenu className="w-6 h-6" />
            )}
          </motion.button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {headerToggle && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden overflow-hidden"
            >
              <div
                className={`py-4 space-y-2 ${
                  isScrolled
                    ? "bg-white border-t border-gray-200"
                    : "bg-primary-800/95 backdrop-blur-md"
                }`}
              >
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={item.path}
                      onClick={() => setHeaderToggle(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                        pathName === item.path
                          ? isScrolled
                            ? "bg-primary-100 text-primary-700"
                            : "bg-white/20 text-white"
                          : isScrolled
                          ? "text-gray-600 hover:text-primary-600 hover:bg-gray-50"
                          : "text-white/90 hover:text-white hover:bg-white/10"
                      }`}
                    >
                      {item.icon}
                      {item.label}
                    </Link>
                  </motion.div>
                ))}

                {token ? (
                  <div className="pt-4 border-t border-gray-200/20 space-y-2">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: navItems.length * 0.1 }}
                    >
                      <Link
                        to="/profile"
                        onClick={() => setHeaderToggle(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                          pathName === "/profile"
                            ? isScrolled
                              ? "bg-primary-100 text-primary-700"
                              : "bg-white/20 text-white"
                            : isScrolled
                            ? "text-gray-600 hover:text-primary-600 hover:bg-gray-50"
                            : "text-white/90 hover:text-white hover:bg-white/10"
                        }`}
                      >
                        <HiUser className="w-4 h-4" />
                        Profile
                      </Link>
                    </motion.div>

                    {isAdmin && (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: (navItems.length + 1) * 0.1 }}
                      >
                        <Link
                          to="/admin/recent-users"
                          onClick={() => setHeaderToggle(false)}
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                            pathName.startsWith("/admin")
                              ? isScrolled
                                ? "bg-primary-100 text-primary-700"
                                : "bg-white/20 text-white"
                              : isScrolled
                              ? "text-gray-600 hover:text-primary-600 hover:bg-gray-50"
                              : "text-white/90 hover:text-white hover:bg-white/10"
                          }`}
                        >
                          <HiCog className="w-4 h-4" />
                          Admin
                        </Link>
                      </motion.div>
                    )}

                    <motion.button
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: (navItems.length + 2) * 0.1 }}
                      onClick={() => {
                        setHeaderToggle(false);
                        handleLogout();
                      }}
                      className="flex items-center gap-3 w-full px-4 py-3 rounded-lg font-medium bg-red-500 hover:bg-red-600 text-white transition-all duration-200"
                    >
                      <HiArrowRightOnRectangle className="w-4 h-4" />
                      Logout
                    </motion.button>
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: navItems.length * 0.1 }}
                    className="pt-4 border-t border-gray-200/20"
                  >
                    <Link
                      to="/login"
                      onClick={() => setHeaderToggle(false)}
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg font-semibold bg-accent-500 hover:bg-accent-600 text-white transition-all duration-200"
                    >
                      Get Started
                    </Link>
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
};

export default Navbar;
